import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { FulfillmentMethodExtend } from "@store/Services/SessionService";
import { getCurrentLanguage, getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import { TYPOGRAPHY } from "@store/theme/design";
import { ProductFulfillmentOption } from "@store/Types/ProductFulfillmentOption";
import React, { FC, useMemo } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    warehouse: string;
    fulfillmentMethod: FulfillmentMethodExtend | FulfillmentMethod;
    isCartLineFulfillment?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const poolcorpSettings = getStoreSettingsCollection(state).poolcorpSettings;

    return {
        displayReadyByFallback: poolcorpSettings.displayReadyByFallback,
        currentLanguage: getCurrentLanguage(state),
    }
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>  & HasProductContext & HasCartLineContext;

export interface ProductReadyByStyles {
    available?: TypographyPresentationProps;
}

export const productReadyByStyles: ProductReadyByStyles = {
    available: {
        color: "success",
        weight: TYPOGRAPHY.weight.bold,
    },
};

const styles = productReadyByStyles;

const ProductReadyBy: FC<Props> = ({
    cartLine,
    productContext,
    warehouse,
    fulfillmentMethod,
    displayReadyByFallback,
    currentLanguage,
    isCartLineFulfillment = false
}) => {
    const product =  productContext.product ? productContext.product : cartLine;
    const fallbackMessage = displayReadyByFallback ? `(${translate("No ETA available")})` : "";
    const languageCode = currentLanguage?.languageCode;

    const productReadyBy = useMemo(() => {
        try {
            const availableFulfillmentOptions: ProductFulfillmentOption[] = JSON.parse(product.properties?.fulfillmentOptions || "");
            const fulfillmentOption = availableFulfillmentOptions.find(option => option.FulfillmentMethod === fulfillmentMethod && option.Warehouse === warehouse);
            const readyByInDays = fulfillmentOption?.ReadyByInDays;

            if (readyByInDays === 0) {
                return translate("Today");
            }

            if (readyByInDays && languageCode) {
                const date = new Date();

                date.setDate(date.getDate() + readyByInDays);

                return date.toLocaleDateString(languageCode, {
                    weekday: "short",
                    day: "numeric",
                    month: "short"
                });
            }

        } catch (error) {
            return undefined;
        }
    }, [product.properties]);

    const labelStyles: TypographyPresentationProps = {
        css: css`
            margin-left: ${isCartLineFulfillment && (fallbackMessage !== "" || productReadyBy) ? '1.8em' : '0'};
        `,
    };

    const availableStyles: TypographyPresentationProps = {
        ...styles.available,
        css: css`
            ${styles.available?.css}
            ${labelStyles?.css}
        `,
    };

    return (
        <>
            {productReadyBy ? (
                <Typography as="span" {...availableStyles}>{productReadyBy}</Typography>
            ) : (
                <Typography as="span" {...labelStyles}>{fallbackMessage}</Typography>
            )}
        </>
    );
};

export default connect(mapStateToProps)(withCartLine(withProductContext(ProductReadyBy)));